/**
 * TestimonialWall — the named-client testimonial module.
 *
 * This is the Blogger Outreach card, lifted out so every page can use it. The
 * point of it is the face: a photo, a real name and a real company read as a
 * person who said something, where "Flavio · B2B SaaS" reads as copywriting.
 *
 * Quotes without a photo still work — they get an initial on a brand plate —
 * so a testimonial never has to wait on an asset to go live.
 */
import { CLIENT_TESTIMONIALS, type ClientTestimonial } from "@/lib/brand";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const CYAN = "#57d7e2";
const glassCyan: React.CSSProperties = {
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(87,215,226,0.32)",
  backdropFilter: "blur(20px)",
};

interface TestimonialWallProps {
  isMobile: boolean;
  /** Defaults to the full roster. Pass a slice to show fewer. */
  items?: ClientTestimonial[];
  /** Cards per row on desktop. */
  columns?: 1 | 2 | 3;
}

export default function TestimonialWall({ isMobile, items = CLIENT_TESTIMONIALS, columns = 2 }: TestimonialWallProps) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : `repeat(${columns}, 1fr)`, gap: "20px" }}>
      {items.map(t => (
        <TestimonialCard key={`${t.name}-${t.company}`} t={t} isMobile={isMobile} />
      ))}
    </div>
  );
}

function TestimonialCard({ t, isMobile }: { t: ClientTestimonial; isMobile: boolean }) {
  return (
    <div style={{
      ...glassCyan, borderRadius: "20px", overflow: "hidden",
      display: "grid", gridTemplateColumns: isMobile ? "1fr" : "240px 1fr",
    }}>
      {t.photo ? (
        <img src={t.photo} alt={t.name} loading="lazy"
          style={{ width: "100%", height: isMobile ? "260px" : "100%", objectFit: "cover", objectPosition: t.photoPos ?? "50% 30%", display: "block" }} />
      ) : (
        // No headshot yet — a brand plate with the initial, so the card keeps
        // its shape instead of collapsing into a bare quote.
        <div style={{
          width: "100%", height: isMobile ? "150px" : "100%", minHeight: isMobile ? undefined : "200px",
          background: "linear-gradient(150deg, rgba(87,215,226,0.16), rgba(32,176,188,0.05))",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <span style={{
            width: 84, height: 84, borderRadius: "50%", background: "linear-gradient(135deg,#85e2e9,#20b0bc)",
            color: "#121212", fontSize: "34px", fontWeight: 800, fontFamily: ff,
            display: "flex", alignItems: "center", justifyContent: "center",
          }} aria-hidden>{t.name.charAt(0)}</span>
        </div>
      )}

      <div style={{ padding: isMobile ? "22px" : "28px", display: "flex", flexDirection: "column", gap: "14px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
          <span style={{ color: "#ffd700", fontSize: "15px", letterSpacing: "2px" }}>★★★★★</span>
          {t.logo && (
            <span style={{ background: "#ffffff", borderRadius: "6px", padding: "5px 9px", display: "inline-flex" }}>
              <img src={t.logo} alt="" loading="lazy" style={{ height: "16px", objectFit: "contain" }} />
            </span>
          )}
        </div>
        <p style={{ color: "rgba(255,255,255,0.72)", fontSize: "16px", fontFamily: ff, lineHeight: 1.65, margin: 0, flex: 1 }}>"{t.quote}"</p>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "13px" }}>
          <span style={{ display: "block", color: "white", fontSize: "15px", fontWeight: 700, fontFamily: ff }}>{t.name}</span>
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "13.5px", fontFamily: ff }}>{t.role}, {t.company}</span>
        </div>
      </div>
    </div>
  );
}

export { CYAN };
